/**
 * One Rowted admin — `docs/rowted-admin-v3.md` §3.2.
 *
 * `<RecordPage>` with `object: 'admin'`. Not "the record idiom, re-drawn for
 * the platform side": the shell itself, the same one a contact and a job use,
 * which is what v3 §1.3's engine change was for. It brings the header, the tab
 * row, the `wide` rule that stands the rail aside for a list tab, the pills
 * read from `GET /platform/records/admin/:id/summary`, and the phone layout
 * that folds the rail into Overview — four things this page used to own copies
 * of and one of which (the phone layout) it did not have at all.
 *
 * **Three of the four tabs are the list engine** (§3.3–3.5): Activity and
 * Clients entered are the `admin-activity` area under two `listKey`s, Sessions
 * is `admin-sessions`. What was a hand-drawn feed with a kind filter is now
 * sortable, filterable, groupable, summable and exportable, because it is the
 * same table every other list in the product is.
 *
 * **What is left for this page to write is a configuration**, which is the
 * record contract's whole claim: a mark, some tags, a meta line, three rail
 * cards and two dialogs.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ApiError, type PlatformRole } from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import {
  Avatar, Button, Card, Tag, type TagTone,
} from '../../components/primitives.js'
import { Modal } from '../../components/overlays.js'
import { Field } from '../../components/fields.js'
import { RecordPage, type RecordConfig } from '../../record/record-page.js'
import { AboutCard } from '../../record/record-rail.js'
import { RelatedList } from '../../components/related-list.js'
import { useRecordSummary } from '../../lib/use-record.js'
import { EM_DASH, formatDocumentDate, formatRelative } from '../../lib/format.js'
import {
  IconClock, IconEdit, IconMail, IconCall, IconMonitor, IconSecurity, IconShieldCheck,
  IconSignOut, IconWarn,
} from '../../frame/icons.js'
import { InviteLink, type Delivered } from './invite-link.js'
import { AdminPage, Loading, can, useAdminUser } from './shell.js'
import { useStepUp } from './step-up.js'

// ---------------------------------------------------------------------------
// What `GET /admins/:id` answers with
// ---------------------------------------------------------------------------

interface Last30Days {
  signIns: number; failed: number; refused: number
  asClient: number; asClientClients: number; fullAccess: number
  changesInside: number; settings: number; integrationKeys: number
}

interface ActivityTenant { id: string; name: string; slug: string }

interface ClientEntered {
  tenant: ActivityTenant
  visits: number
  lastAt: string
  fullAccess: number
}

interface SecurityCard {
  recoveryCodesLeft: number
  recoveryCodesTotal: number
  passwordChangedAt: string | null
  lastRoleChangeAt: string | null
  lastRoleChange: string | null
}

interface AdminDetail {
  id: string
  firstName: string
  lastName: string
  name: string
  email: string
  role: PlatformRole
  status: string
  mobilePhone: string | null
  mfaEnrolled: boolean
  lastSignInAt: string | null
  invitedAt: string | null
  invitedByName: string | null
  createdAt: string
  sessions: number
  last30Days: Last30Days
  clientsEntered: ClientEntered[]
  security: SecurityCard
}

interface AdminSession {
  id: string
  device: string
  browser: string
  ip: string | null
  createdAt: string
  lastSeenAt: string
  revokedAt: string | null
  revokedReason: string | null
  state: 'now' | 'idle' | 'ended'
  current: boolean
}

const ROLE_WORDS: Record<PlatformRole, string> = {
  owner: 'Owner', admin: 'Admin', support: 'Support',
  billing: 'Billing', readonly: 'Read-only',
}

/** What each role can do, in one line — §2.3's words, for the role picker. */
const ROLE_NOTES: Record<PlatformRole, string> = {
  owner: 'Everything, including keys and admins',
  admin: 'Everything but integration keys and admins',
  support: 'Read, and sign in as a client',
  billing: 'Read, and change plans and pricing',
  readonly: 'Read only',
}

/**
 * A role's colour, from the tokens rather than from a hex.
 *
 * `--status-job-*` because those are the pairs §3.1 already defines and the
 * design-token test insists nothing hand-types one: a role is not a job status,
 * but "the purple pair" and "the blue pair" are the product's colours whatever
 * names they were first given. The alternative — five new variables for five
 * roles — is five more things that can drift from the palette.
 */
const pair = (name: string) => ({
  fg: `rgb(var(--status-${name}-fg))`,
  bg: `rgb(var(--status-${name}-bg))`,
})

const ROLE_TONE: Record<PlatformRole, { fg: string; bg: string }> = {
  owner: pair('job-invoiced'),
  admin: pair('job-paid'),
  support: pair('job-quoted'),
  billing: pair('job-scheduled'),
  readonly: {
    fg: 'rgb(var(--color-text-muted))',
    bg: 'rgb(var(--color-line-soft))',
  },
}

const STATUS_TONE: Record<string, { fg: string; bg: string }> = {
  active: pair('job-paid'),
  invited: { fg: 'rgb(var(--color-warn))', bg: 'rgb(var(--color-warn-bg))' },
  suspended: { fg: 'rgb(var(--color-danger))', bg: 'rgb(var(--color-danger-bg))' },
}

/**
 * The shape the config closes over: the payload, the devices the rail peeks at,
 * and what only this page can do.
 */
export interface AdminRecordData extends AdminDetail {
  devices: AdminSession[]
  mayWrite: boolean
  onEdit: () => void
  onChangeRole: () => void
  onRevokeAll: () => void
  onSuspend: () => void
  onResend: () => void
  onOpenTab: (tab: string) => void
}

// ---------------------------------------------------------------------------

export function AdminProfile() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { me, ready } = useAdminUser()
  const stepUp = useStepUp()

  const [admin, setAdmin] = useState<AdminDetail | null>(null)
  const [devices, setDevices] = useState<AdminSession[]>([])
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [changingRole, setChangingRole] = useState(false)
  /* Only when a resent invite could not be emailed; see `InviteLink`. */
  const [undelivered, setUndelivered] = useState<Delivered | null>(null)

  /*
   * The pills come from the summary endpoint, not from this page's own payload.
   * `adminRequest` is passed because the path is the same shape on both sides —
   * `/records/admin/:id/summary` — and only the base and the CSRF header
   * differ. That is the whole of §1.3: one hook, two sides.
   */
  const { summary } = useRecordSummary('admin', id || undefined, { request: adminRequest })

  const load = useCallback(async () => {
    setError(null)
    try {
      const [detail, sessions] = await Promise.all([
        adminRequest<AdminDetail>(`/admins/${id}`),
        adminRequest<{ items: AdminSession[] }>(`/admins/${id}/sessions`),
      ])
      setAdmin(detail)
      setDevices(sessions.items)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That admin could not be read.')
    }
  }, [id])

  useEffect(() => { if (ready && me) void load() }, [ready, me, load])

  const mayWrite = can(me, 'admins')

  const act = useCallback(async (run: () => Promise<void>) => {
    setError(null)
    try {
      await stepUp.guard(run)
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not go through.')
    }
  }, [load, stepUp])

  /*
   * **There is no `openInside` here any more.**
   *
   * Opening a record inside a customer from this page meant starting a support
   * session as a side effect of a click on a feed row — no reason typed, no
   * consent recorded, a 15-minute window nobody asked for. Getting into a
   * customer is **Sign in as** on the client record, which asks for the reason
   * and the consent first, and the Clients entered tab's tray links there.
   */

  /* The tab is `RecordPage`'s to own; this only needs to be able to ask. */
  const openTab = useCallback((tab: string) => {
    navigate(`/admin/admins/${id}?tab=${tab}`, { replace: true })
  }, [id, navigate])

  const record = useMemo<AdminRecordData | null>(() => (admin ? {
    ...admin,
    devices,
    mayWrite,
    onEdit: () => setEditing(true),
    onChangeRole: () => setChangingRole(true),
    onRevokeAll: () => void act(() => adminRequest(
      `/admins/${admin.id}/revoke-sessions`, { method: 'POST', body: {} })),
    onSuspend: () => void act(() => adminRequest(`/admins/${admin.id}`, {
      method: 'PATCH',
      body: { status: admin.status === 'suspended' ? 'active' : 'suspended' },
    })),
    /*
     * The same reporting path a first invite takes: the response says whether
     * the email went, and hands the link over when it did not. A resend that
     * said "on its way" into a deployment with no mailbox would be the bug
     * this page was added to fix, drawn twice.
     */
    onResend: () => void act(async () => {
      const answer = await adminRequest<Delivered>(
        `/admins/${admin.id}/resend-invite`, { method: 'POST', body: {} })
      if (!answer.delivered) setUndelivered({ ...answer, email: admin.email })
    }),
    onOpenTab: openTab,
  } : null), [admin, devices, mayWrite, act, openTab])

  const config = useMemo(() => adminConfig(), [])

  if (!ready || !record) {
    return (
      <AdminPage me={me} active="admins">
        {error ? <Problem note={error} /> : <Loading />}
      </AdminPage>
    )
  }

  return (
    <AdminPage me={me} active="admins">
      {error && (
        <p role="alert" className="m-0 border-b border-line bg-danger-bg px-6 py-2 text-cell
                                   text-danger">
          {error}
        </p>
      )}

      <RecordPage config={config} record={record} summary={summary}>
        {(tab) => (
          tab === 'overview'
            ? <OverviewTab record={record} />
            /*
              * One area, two of these three tabs. Clients entered is Activity
              * with `kind = as_client` and a `listKey` of its own, so its
              * columns and its saved views are its own while the rows, the
              * words and the tray stay one definition (§3.5).
              */
            : tab === 'activity'
              ? (
                <RelatedList
                  area="admin-activity"
                  title="Activity"
                  variant="tab"
                  request={adminRequest}
                  listKey="activity@admin"
                  baseFilter={[{ field: 'platformUserId', op: 'eq', value: record.id }]}
                  fields={['at', 'title', 'tenantName', 'access', 'device']}
                  emptyTitle="Nothing recorded yet."
                  emptyBody="Sign-ins, settings changes and support sessions all land here."
                />
              )
              : tab === 'sessions'
                ? (
                  <RelatedList
                    area="admin-sessions"
                    title="Sessions"
                    variant="tab"
                    request={adminRequest}
                    listKey="sessions@admin"
                    baseFilter={[{ field: 'platformUserId', op: 'eq', value: record.id }]}
                    fields={['device', 'place', 'status', 'lastSeenAt', 'steppedUpAt']}
                    emptyTitle="No devices."
                    emptyBody="A session appears here the first time they sign in."
                  />
                )
                : (
                  <RelatedList
                    area="admin-activity"
                    title="Clients entered"
                    variant="tab"
                    request={adminRequest}
                    listKey="clients-entered@admin"
                    baseFilter={[
                      { field: 'platformUserId', op: 'eq', value: record.id },
                      { field: 'kind', op: 'eq', value: 'as_client' },
                    ]}
                    fields={['at', 'tenantName', 'access', 'reason', 'actionCount']}
                    emptyTitle="They have never signed in as a client."
                    emptyBody="Every visit is listed here, with what was changed inside it."
                  />
                )
        )}
      </RecordPage>

      {changingRole && (
        <RoleDialog
          admin={record}
          onClose={() => setChangingRole(false)}
          onSave={(role) => {
            setChangingRole(false)
            void act(() => adminRequest(`/admins/${record.id}`, {
              method: 'PATCH', body: { role },
            }))
          }}
        />
      )}
      {editing && (
        <EditDialog
          admin={record}
          onClose={() => setEditing(false)}
          onSave={(body) => {
            setEditing(false)
            void act(() => adminRequest(`/admins/${record.id}`, { method: 'PATCH', body }))
          }}
        />
      )}
      {undelivered?.link && (
        <InviteLink
          answer={{ ...undelivered, link: undelivered.link }}
          onClose={() => setUndelivered(null)}
        />
      )}
      {stepUp.dialog}
    </AdminPage>
  )
}

const Problem = ({ note }: { note: string }) => (
  <p role="alert" className="m-0 p-4 text-body text-danger">{note}</p>
)

// ---------------------------------------------------------------------------
// The configuration
// ---------------------------------------------------------------------------

/**
 * No `navigate` argument, unlike the tenant records' configs.
 *
 * Nothing on this page navigates from a config callback: the breadcrumb is an
 * `href`, the three actions are writes, and the rail's "All ›" asks for a tab
 * rather than a route. So the config is a constant and `useMemo` holds one of
 * it for the life of the page.
 */
export function adminConfig(): RecordConfig<AdminRecordData> {
  return {
    object: 'admin',

    breadcrumb: (record) => [
      { label: 'Admin users', href: '/admin/admins' },
      { label: record.name },
    ],

    mark: (record) => <Avatar name={record.name} size={56} />,

    title: (record) => record.name,

    tags: (record) => [
      { label: ROLE_WORDS[record.role], tone: ROLE_TONE[record.role] },
      {
        label: record.status[0]!.toUpperCase() + record.status.slice(1),
        tone: STATUS_TONE[record.status] ?? STATUS_TONE.active!,
      },
    ],

    meta: (record) => [
      { icon: <IconMail size={14} />, text: record.email, href: `mailto:${record.email}` },
      {
        icon: <IconShieldCheck size={14} />,
        text: record.mfaEnrolled ? 'Authenticator enrolled' : 'No authenticator yet',
      },
      {
        icon: <IconClock size={14} />,
        text: record.lastSignInAt
          ? `Last sign-in ${formatRelative(record.lastSignInAt)}`
          : 'Has never signed in',
      },
      ...(record.mobilePhone
        ? [{ icon: <IconCall size={14} />, text: record.mobilePhone }]
        : []),
    ],

    primary: (record) => (record.mayWrite ? {
      label: 'Change role',
      icon: <IconSecurity size={15} />,
      onClick: record.onChangeRole,
    } : null),

    actions: (record) => (record.mayWrite ? [
      /*
       * **Only while they are still invited.** `POST /resend-invite` answers
       * 409 once somebody has accepted, so an always-on button would be a
       * control that is right to press exactly once and refuses for ever
       * after. First in the list because on an invited row it is the only
       * thing anybody came here to do.
       */
      ...(record.status === 'invited' ? [{
        label: 'Resend invite',
        icon: <IconMail size={15} />,
        onClick: record.onResend,
      }] : []),
      {
        label: 'Revoke sessions',
        icon: <IconSignOut size={15} />,
        onClick: record.onRevokeAll,
      },
      {
        label: record.status === 'suspended' ? 'Restore' : 'Suspend',
        icon: <IconWarn size={15} />,
        onClick: record.onSuspend,
      },
      { label: 'Edit', icon: <IconEdit size={15} />, onClick: record.onEdit },
    ] : []),

    /*
     * Three `wide` tabs and one that is not (§3.2). The counts come from the
     * summary rather than from the payload, so the number on the tab and the
     * number of rows the tab lists are the same query.
     */
    tabs: (record, summary) => [
      { key: 'overview', label: 'Overview', icon: 'home' },
      {
        key: 'activity',
        label: 'Activity',
        icon: 'clock',
        wide: true,
        ...(summary ? { count: summary.counts['admin-activity'] ?? 0 } : {}),
      },
      {
        key: 'sessions',
        label: 'Sessions',
        icon: 'access',
        wide: true,
        count: summary?.counts['admin-sessions'] ?? record.sessions,
      },
      {
        key: 'clients',
        label: 'Clients entered',
        icon: 'company',
        wide: true,
        /*
         * Visits, from the summary. `clientsEntered` is grouped by customer
         * for the rail's peek — two visits to one company is one row there and
         * two rows on this tab, and the tab's number belongs to the tab.
         */
        count: summary?.counts['clients-entered'] ?? record.clientsEntered.length,
      },
    ],

    /*
     * About and Security (§3.2). The four "what have they been doing" cards are
     * the Overview's *body* — the doc lists Security in both places, and the
     * rail is where it belongs: it answers "is this account safe", which is a
     * fact about the person and not about the last thirty days.
     */
    rail: (record) => (
      <>
        <AboutCard
          onEdit={record.mayWrite ? record.onEdit : undefined}
          fields={[
            /*
             * The word alone. It used to carry `ROLE_NOTES` after a middot and
             * the About card's 96px label column clipped it to "Owner ·
             * Everything, including k…" — a sentence cut mid-word says less
             * than no sentence. Change role spells each one out, in a dialog
             * with room.
             */
            { label: 'Role', value: ROLE_WORDS[record.role] },
            {
              label: 'Invited by',
              value: record.invitedByName
                ?? (record.invitedAt ? 'Somebody since removed' : 'Made from the command line'),
            },
            { label: 'Since', value: formatDocumentDate(record.createdAt) },
            { label: 'Mobile', value: record.mobilePhone ?? EM_DASH },
          ]}
        />
        <SecurityRail admin={record} />
      </>
    ),

    phone: {
      quickActions: (record) => (record.mayWrite ? [
        { label: 'Role', icon: <IconSecurity size={16} />, onClick: record.onChangeRole },
        { label: 'Edit', icon: <IconEdit size={16} />, onClick: record.onEdit },
        { label: 'Sign out', icon: <IconSignOut size={16} />, onClick: record.onRevokeAll },
      ] : []),
      tabBar: ['overview', 'activity', 'sessions', 'clients'],
    },
  }
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

/**
 * What this person has been doing, in three cards (§3.2).
 *
 * These were the v2 rail; the rail is About and Security now. It is the right
 * way round: a thirty-day tally is the *substance* of an admin's record, and
 * substance belongs in the column that has room for it.
 */
function OverviewTab({ record }: { record: AdminRecordData }) {
  return (
    <div className="grid gap-3.5 md:grid-cols-2">
      <Last30Card counts={record.last30Days} />
      <ClientsCard
        entered={record.clientsEntered}
        onAll={() => record.onOpenTab('clients')}
      />
      <DevicesCard
        sessions={record.devices}
        mayRevoke={record.mayWrite}
        onRevokeAll={record.onRevokeAll}
        onAll={() => record.onOpenTab('sessions')}
      />
    </div>
  )
}

/*
 * `Sessions` and `ClientsEntered` were two hand-drawn lists; they are
 * `RelatedList` over `admin-sessions` and `admin-activity` now (v3 §3.4–3.5),
 * which is where the sorting, the filter, the Σ row and the export came from.
 * `STATE_TONE` stayed: the rail's Devices card below is a five-row peek, not
 * a list, so it still draws its own tag.
 */

const STATE_TONE: Record<AdminSession['state'], TagTone> = {
  now: 'ok', idle: 'quiet', ended: 'plain',
}

function RailCard({ title, action, children }: {
  title: string
  action?: { label: string; onClick: () => void }
  children: React.ReactNode
}) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center gap-2 px-[18px] pb-3 pt-3.5">
        <b className="text-card">{title}</b>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="ml-auto text-cell font-semibold text-accent hover:text-accent-dark"
          >
            {action.label}
          </button>
        )}
      </div>
      <div className="px-[18px] pb-3">{children}</div>
    </Card>
  )
}

function RailRow({ label, value, tone }: {
  label: string; value: string; tone?: 'danger' | 'warn'
}) {
  return (
    <div className="flex items-baseline justify-between border-b border-line-soft py-1.5
                    text-cell last:border-b-0">
      <span className="text-text-muted">{label}</span>
      <b className={tone === 'danger' ? 'tnum text-danger'
        : tone === 'warn' ? 'tnum text-warn' : 'tnum text-text'}>
        {value}
      </b>
    </div>
  )
}

function Last30Card({ counts }: { counts: Last30Days }) {
  return (
    <RailCard title="Last 30 days">
      <RailRow label="Sign-ins" value={String(counts.signIns)} />
      <RailRow label="Failed sign-ins" value={String(counts.failed)}
        {...(counts.failed > 0 ? { tone: 'danger' as const } : {})} />
      <RailRow
        label="Signed in as a client"
        value={counts.asClient === 0
          ? '0'
          : `${counts.asClient} · ${counts.asClientClients} `
            + `${counts.asClientClients === 1 ? 'client' : 'clients'}`}
      />
      <RailRow label="… with full access" value={String(counts.fullAccess)}
        {...(counts.fullAccess > 0 ? { tone: 'warn' as const } : {})} />
      <RailRow label="Changes inside clients" value={String(counts.changesInside)} />
      <RailRow label="Platform settings changed" value={String(counts.settings)} />
      <RailRow label="Integration keys changed" value={String(counts.integrationKeys)} />
      <RailRow label="Refused actions" value={String(counts.refused)}
        {...(counts.refused > 0 ? { tone: 'danger' as const } : {})} />
    </RailCard>
  )
}

/**
 * The top five clients they have been inside, and a way to the rest.
 *
 * A row is a **link to the client**, not a way into it: the tenant record is
 * where Sign in as lives, with the reason and the consent it asks for. It used
 * to open a support session on a click, which is exactly the thing that ought
 * to take a sentence of typing.
 */
function ClientsCard({ entered, onAll }: {
  entered: ClientEntered[]
  onAll: () => void
}) {
  return (
    <RailCard
      title="Clients they have entered"
      {...(entered.length > 0 ? { action: { label: 'All ›', onClick: onAll } } : {})}
    >
      {entered.length === 0
        ? <p className="m-0 py-1.5 text-cell text-text-muted">None yet.</p>
        : entered.slice(0, 5).map((one) => (
          <Link
            key={one.tenant.id}
            to={`/admin/clients/${one.tenant.id}`}
            className="flex w-full items-center gap-2.5 border-b border-line-soft py-1.5
                       text-left text-cell text-text no-underline last:border-b-0
                       hover:text-accent-dark"
          >
            <Avatar name={one.tenant.name} size={24} tone="accent" />
            <span className="flex-1 truncate">{one.tenant.name}</span>
            <span className="tnum text-text-muted">{one.visits}×</span>
            <span className="text-[11.5px] text-text-faint">{formatRelative(one.lastAt)}</span>
          </Link>
        ))}
    </RailCard>
  )
}

function DevicesCard({ sessions, mayRevoke, onRevokeAll, onAll }: {
  sessions: AdminSession[]
  mayRevoke: boolean
  onRevokeAll: () => void
  onAll: () => void
}) {
  const live = sessions.filter((one) => one.state !== 'ended')
  return (
    <RailCard
      title="Devices and sessions"
      action={mayRevoke && live.length > 0
        ? { label: 'Revoke all', onClick: onRevokeAll }
        : { label: 'All ›', onClick: onAll }}
    >
      {sessions.length === 0
        ? <p className="m-0 py-1.5 text-cell text-text-muted">No devices.</p>
        : sessions.slice(0, 5).map((session) => (
          <div
            key={session.id}
            className="flex items-center gap-2.5 border-b border-line-soft py-1.5 text-cell
                       last:border-b-0"
          >
            <span className="text-text-muted"><IconMonitor size={15} /></span>
            <span className="flex min-w-0 flex-1 flex-col gap-px">
              <span className="truncate">{session.device} · {session.browser}</span>
              <span className="truncate text-[11.5px] text-text-faint">
                {session.revokedAt
                  ? `signed out ${formatRelative(session.revokedAt)}`
                  : [session.ip, `last ${formatRelative(session.lastSeenAt)}`]
                    .filter(Boolean).join(' · ')}
              </span>
            </span>
            <Tag tone={STATE_TONE[session.state]} className="h-[19px] text-[11px] font-semibold">
              {session.state}
            </Tag>
          </div>
        ))}
    </RailCard>
  )
}

function SecurityRail({ admin }: { admin: AdminDetail }) {
  const { security } = admin
  return (
    <RailCard title="Security">
      <RailRow
        label="Two-step"
        value={admin.mfaEnrolled ? 'Authenticator' : 'Not enrolled'}
        {...(admin.mfaEnrolled ? {} : { tone: 'danger' as const })}
      />
      <RailRow
        label="Recovery codes"
        value={security.recoveryCodesTotal === 0
          ? 'None issued'
          : `${security.recoveryCodesLeft} of ${security.recoveryCodesTotal} left`}
        {...(security.recoveryCodesTotal > 0 && security.recoveryCodesLeft <= 2
          ? { tone: 'warn' as const }
          : {})}
      />
      <RailRow
        label="Password changed"
        value={security.passwordChangedAt
          ? formatDocumentDate(security.passwordChangedAt)
          : 'Not since they set it'}
      />
      {/* Invited by is the About card's, two cards up. One fact, one place. */}
      <RailRow
        label="Role changed"
        value={security.lastRoleChange && security.lastRoleChangeAt
          ? `${security.lastRoleChange} · ${formatDocumentDate(security.lastRoleChangeAt)}`
          : 'Never'}
      />
    </RailCard>
  )
}

// ---------------------------------------------------------------------------
// The two dialogs
// ---------------------------------------------------------------------------

/**
 * Change role — the primary action.
 *
 * Owner is offered here and not on the invite (§5.3): an owner is promoted by
 * somebody who is already looking at the person, not handed out with an email
 * address. The server refuses the last owner's demotion whatever this offers.
 */
function RoleDialog({ admin, onClose, onSave }: {
  admin: AdminDetail
  onClose: () => void
  onSave: (role: PlatformRole) => void
}) {
  const [role, setRole] = useState<PlatformRole>(admin.role)
  return (
    <Modal
      open
      onClose={onClose}
      title={`Change ${admin.firstName || admin.name}’s role`}
      subtitle="A role is what they can do. It takes effect on their next request."
      width={460}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={role === admin.role}
            onClick={() => onSave(role)}
          >
            Save role
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-1.5 p-4">
        {(Object.keys(ROLE_WORDS) as PlatformRole[]).map((one) => (
          <label
            key={one}
            className="flex cursor-pointer items-start gap-2.5 rounded-field border
                       border-line px-3 py-2.5 hover:border-line-strong
                       has-[:checked]:border-accent-line has-[:checked]:bg-accent-soft"
          >
            <input
              type="radio"
              name="role"
              value={one}
              checked={role === one}
              onChange={() => setRole(one)}
              className="mt-0.5 accent-accent"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-body font-semibold">{ROLE_WORDS[one]}</span>
              <span className="text-[11.5px] text-text-muted">{ROLE_NOTES[one]}</span>
            </span>
          </label>
        ))}
      </div>
    </Modal>
  )
}

/** First name and last name, everywhere a person is edited — §0.10. */
function EditDialog({ admin, onClose, onSave }: {
  admin: AdminDetail
  onClose: () => void
  onSave: (body: Record<string, string>) => void
}) {
  const [firstName, setFirstName] = useState(admin.firstName)
  const [lastName, setLastName] = useState(admin.lastName)
  const [email, setEmail] = useState(admin.email)
  const [mobilePhone, setMobilePhone] = useState(admin.mobilePhone ?? '')

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${admin.firstName || admin.name}`}
      width={480}
      footer={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            /*
             * Both names, per §0.10 — and because the server's own schema takes
             * `min(1)` for each, so an empty last name is a 400 rather than a
             * save. A button that cannot work does not light up.
             */
            disabled={firstName.trim() === '' || lastName.trim() === ''
              || email.trim() === ''}
            onClick={() => onSave({
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              email: email.trim(),
              mobilePhone: mobilePhone.trim(),
            })}
          >
            Save
          </Button>
        </>
      )}
    >
      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <Field label="First name" value={firstName} onChange={setFirstName} autoFocus />
        <Field label="Last name" value={lastName} onChange={setLastName} />
        <div className="sm:col-span-2">
          <Field label="Email" value={email} onChange={setEmail} />
        </div>
        <div className="sm:col-span-2">
          <Field
            label="Mobile"
            value={mobilePhone}
            onChange={setMobilePhone}
            hint="For “send a test SMS to me” on the Twilio card."
          />
        </div>
      </div>
    </Modal>
  )
}
